import {
  Box,
  IconButton,
  Modal,
  Paper,
  Collapse,
  Chip,
  Divider,
  Tooltip,
} from "@mui/material";
import { useRef, useState } from "react";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { v4 as uuidv4 } from "uuid";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import {
  Crop,
  Download,
  Edit,
  UploadFile,
  Visibility,
} from "@mui/icons-material";
import { Button, TextField, Typography } from "Atoms";
import { getTrimmedString } from "Utils/helperFunctions";
import ImageCropper from "Components/ImageCropper";

/* ------------------------------------------------------------------ */
/* 💡  Types                                                          */
/* ------------------------------------------------------------------ */
interface Attachment {
  id: string; // local UUID
  title: string;
  description: string;
  file?: File; // for newly added or edited attachments
  url?: string; // for existing remote attachments
  mimetype?: string; // optional – helpful for crop feature
}

interface AttachmentManagerProps {
  _attachments?: Attachment[]; // list managed in the parent
  onChange: (updated: Attachment[]) => void;

  disableFileChangeForExisting?: boolean; // true = editing remote file blocked
  defaultExpanded?: boolean; // accordion initial state
  allowedFileExtensions?: string[]; // [] = accept any
  cropFeature?: boolean; // enable image-cropper button
  previewImages?: boolean; // NEW PROP
  readOnly?: boolean; // Only view, no edits
  downloadAble?: boolean; // show a download icon for remote files
  title?: string;
  showImageThumbs?: boolean;
  multiple?: boolean; // allow picking several files at once in the file picker
}

/* ------------------------------------------------------------------ */
/* 📦  Component                                                      */
/* ------------------------------------------------------------------ */
const AttachmentManager = ({
  _attachments = [],
  onChange,
  disableFileChangeForExisting = false,
  defaultExpanded = false,
  allowedFileExtensions = [],
  cropFeature = false,
  previewImages = false, // NEW PROP
  readOnly = false,
  downloadAble = true,
  title = "Attachments",
  showImageThumbs = false,
  multiple = false,
}: AttachmentManagerProps) => {
  /* make it shorter in code below */
  const attachments = _attachments;

  /* ---------------- state ---------------------------------------- */
  const [previewTarget, setPreviewTarget] = useState<string | null>(null); // NEW
  const [editMode, setEditMode] = useState<boolean>(false); // in modal?
  const [expanded, setExpanded] = useState(defaultExpanded); // accordion
  const [isDragOver, setIsDragOver] = useState(false); // drop color
  const [open, setOpen] = useState(false); // modal flag
  // Draft of the row being added/edited in the Upload modal. Only merged into
  // the parent list (via onChange) when the modal's Save runs handleUpdate.
  const [selectedAttachment, setSelectedAttachment] =
    useState<Attachment | null>(null);

  // What the modal's file row displays. CROP-NOTE: this is polymorphic and
  // the main source of confusion in the crop flow — it can hold:
  //   - a raw `File`         → freshly picked via the hidden input, or the
  //                            result of a crop (ImageCropper onSave)
  //   - an `Attachment` row  → set by the Edit icon (setSelectedFile(att));
  //                            has `.id`, and either `.url` (remote, already
  //                            uploaded) or `.file` (local, not uploaded yet)
  //   - null                 → nothing chosen
  // Code below probes both shapes (`.type` vs `.mimetype`, `.name` vs
  // `.file?.name`, `.id`, `.url`).
  const [selectedFile, setSelectedFile] = useState<File | any>(null);
  const fileInputRef = useRef<HTMLInputElement>(null); // hidden input

  /* cropping
   * Flow: Crop icon in the modal → setCropTarget → <ImageCropper> modal
   * renders → user clicks its Save → ImageCropper builds a PNG `File` and
   * calls onSave(cropped) (see the IMAGE CROPPER modal at the bottom) →
   * cropped file is written into selectedFile/selectedAttachment (for the
   * Upload modal's Save) AND straight into the parent list via
   * updateAttachmentFile.
   *
   * cropTarget.id        → row id used by updateAttachmentFile to find the
   *                        attachment to replace in the parent list
   * cropTarget.fileOrUrl → image source handed to ImageCropper: a signed URL
   *                        string for remote rows, otherwise a File
   */
  const [cropTarget, setCropTarget] = useState<{
    id: string;
    fileOrUrl: File | string;
  } | null>(null);

  /* ---------------- helpers -------------------------------------- */

  /** Validate extension vs. prop list (case-insensitive) */
  const isExtAllowed = (file: File) => {
    if (allowedFileExtensions.length === 0) return true;
    const ext = file.name.split(".").pop()?.toLowerCase();
    return allowedFileExtensions
      .map((e) => e.toLowerCase())
      .includes(ext ?? "");
  };

  /** Accept attribute for the `<input type="file">` element */
  const acceptAttr =
    allowedFileExtensions.length === 0
      ? undefined
      : allowedFileExtensions.map((e) => "." + e).join(",");

  /* ---------------- DnD handlers --------------------------------- */

  /** Handle files dropped onto the main drop zone */
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);

    const files = Array.from(e.dataTransfer.files).filter(isExtAllowed);
    if (!files.length) return;

    const newAttachments: Attachment[] = files.map((file) => ({
      id: uuidv4(),
      title: file.name,
      description: "",
      file,
    }));

    onChange([...attachments, ...newAttachments]);
  };

  const isImage = (att: Attachment) => {
    if (att.mimetype) return att.mimetype.startsWith("image/");
    if (att.file) return att.file.type.startsWith("image/");
    if (att.url) return /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(att.url);
    return false;
  };

  /* ---------------- update / delete helpers ---------------------- */

  /** Replace the `file` of an existing attachment (crop use-case).
   *  Remote rows (url, no file) become local rows (file, no url), which
   *  parents' getAttachmentChanges reads as "replace" (toReplace: re-upload
   *  the new bytes to the same fileid via uploadFilesAPIByFileId).
   *  CROP-NOTE: commits to the parent immediately via onChange — it does not
   *  wait for the Upload modal's Save, so Cancel can't roll a crop back.
   *  If `id` matches no row (see the Crop icon handler), nothing changes. */
  const updateAttachmentFile = (id: string, newFile: File) => {
    const updated = attachments.map((att: Attachment) => {
      const remote = !!att.url && !att.file; // remote-only item
      if (att.id !== id) return att;

      /* replace remote => treat as new local */
      if (remote) return { ...att, file: newFile, url: undefined };
      /* replace local file => just swap File */
      return { ...att, file: newFile };
    });
    onChange(updated);
  };

  /** Remove attachment entirely */
  const handleDelete = (id: string) =>
    onChange(attachments.filter((a) => a.id !== id));

  /** Reset modal state */
  const handleClose = () => {
    setOpen(false);
    setEditMode(false);
    setSelectedAttachment(null);
    setSelectedFile(null);
  };

  /** Save / update clicked in modal */
  const handleUpdate = () => {
    if (!selectedAttachment) return;

    /* ---------------- work out final file object ----------------
     * Resolution order, matching the shapes selectedFile can hold:
     *   1. selectedFile.file  → selectedFile is an Attachment row (Edit icon)
     *   2. selectedFile       → a raw File (picked, or the crop result)
     *   3. selectedAttachment.file → fallback from the draft row
     * Undefined for an edited remote row that wasn't changed (keeps url). */
    const file =
      selectedFile?.file ??
      (selectedFile instanceof File ? selectedFile : undefined) ??
      selectedAttachment.file;

    /* ---------------- auto-fill title if empty ------------------ */
    let finalTitle = selectedAttachment.title?.trim();
    if (!finalTitle) {
      if (file) finalTitle = file.name;
      else if (selectedAttachment.url)
        finalTitle = selectedAttachment.url.split("/").pop() ?? "untitled";
      else finalTitle = "untitled";
    }

    const updatedAttachment: Attachment = {
      ...selectedAttachment,
      title: finalTitle,
      file,
      // keep remote url only if we didn't set a new file
      url: file ? undefined : selectedAttachment.url,
    };

    /* ---------------- merge back into list ---------------------- */
    const exists = attachments.some((a) => a.id === updatedAttachment.id);
    const updated = exists
      ? attachments.map((a) =>
          a.id === updatedAttachment.id ? updatedAttachment : a
        )
      : [...attachments, updatedAttachment];

    onChange(updated);
    handleClose();
  };

  /* ---------------- derived: selected file size (for modal) ----- */
  const selectedFileSize = selectedFile?.size ?? selectedFile?.file?.size ?? 0;

  /* ------------------------------------------------------------------ */
  /* JSX                                                                */
  /* ------------------------------------------------------------------ */
  return (
    <>
      {/* ===== PANEL HEADER (toggle) ================================== */}
      <Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
            cursor: "pointer",
          }}
          onClick={() => setExpanded(!expanded)}
        >
          <Typography variant="h6">
            <b>{title}</b>&nbsp;
            <Chip
              label={<b>{attachments.length}</b>}
              color="primary"
              size="small"
            />
          </Typography>
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </Box>

        {/* ===== COLLAPSIBLE SECTION ================================= */}
        <Collapse in={expanded}>
          {attachments.length > 0 && (
            <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
              {readOnly ? (
                ""
              ) : (
                <Button variant="contained" onClick={() => setOpen(true)}>
                  <UploadFile fontSize="small" sx={{ mr: 1 }} />
                  UPLOAD FILE
                </Button>
              )}
            </Box>
          )}

          {/* ---- DROP ZONE ----------------------------------------- */}
          <Box
            onDrop={handleDrop}
            onDragOver={(e) => {
              e.preventDefault();
              setIsDragOver(true);
            }}
            onDragLeave={() => setIsDragOver(false)}
            sx={{
              border: "1px solid #ccc",
              borderRadius: 2,
              p: 4,
              mb: 3,
              textAlign: "center",
              backgroundColor: isDragOver ? "lightgrey" : "#fff",
              transition: "background-color 0.3s ease",
            }}
          >
            {/* ----- Empty state ------------ */}
            {attachments.length === 0 && (
              <>
                <Typography variant="h6">
                  There are no files yet
                </Typography>
                {readOnly ? (
                  ""
                ) : (
                  <Typography variant="body2" color="text.secondary" mt={1}>
                    {multiple
                      ? "Select one or more files, or drag & drop them here."
                      : "Upload one file. Tip: You can also upload multiple simultaneously via drag & drop."}
                  </Typography>
                )}
                {readOnly ? (
                  ""
                ) : (
                  <Button
                    variant="contained"
                    sx={{ mt: 2 }}
                    onClick={() => setOpen(true)}
                  >
                    <UploadFile fontSize="small" sx={{ mr: 1 }} /> UPLOAD FILE
                  </Button>
                )}
              </>
            )}

            {/* ----- List of attachments ---- */}
            {attachments.map((att) => (
              <div key={att.id}>
                <Box display="flex" justifyContent="space-between" textAlign={"start"} gap={2}>
                  {/* left side: title & description */}
                  <Box display="flex" gap={2} minWidth={0}>
                    {showImageThumbs && isImage(att) && (att.url || att.file) ? (
                      <Box
                        sx={{
                          width: 84,
                          height: 84,
                          borderRadius: 2,
                          overflow: "hidden",
                          flexShrink: 0,
                          bgcolor: "grey.100",
                          border: "1px solid",
                          borderColor: "divider",
                        }}
                      >
                        <img
                          src={att.file ? URL.createObjectURL(att.file) : att.url}
                          alt={att.title}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            display: "block",
                          }}
                        />
                      </Box>
                    ) : null}
                    <Box minWidth={0}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {getTrimmedString(att.title, 30)}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ textAlign: "start" }}
                    >
                      {att.description || "(Optional text)"}
                    </Typography>
                    </Box>
                  </Box>

                  {/* action icons */}
                  <Box flexShrink={0}>
                    {previewImages && isImage(att) && (
                        <Tooltip title="View">
                      <IconButton
                        onClick={() => {
                          if (att.file) {
                            const url = URL.createObjectURL(att.file);
                            setPreviewTarget(url);
                          } else if (att.url) {
                            setPreviewTarget(att.url);
                          }
                        }}
                      >
                        <Visibility fontSize={"small"} />
                      </IconButton>
                      </Tooltip>
                    )}
                    {downloadAble ? (
                      <Tooltip title="Download">
                        <span>
                          <IconButton
                            component="a"
                            href={att.url}
                            download
                            target="_blank"
                            rel="noreferrer"
                            disabled={!att.url}
                          >
                            <Download />
                          </IconButton>
                        </span>
                      </Tooltip>
                    ) : (
                      ""
                    )}

                    {readOnly ? (
                      ""
                    ) : (
                      <Tooltip title="Edit">
                      <IconButton
                        onClick={() => {
                          setSelectedAttachment(att);
                          setSelectedFile(att); // may contain .file or .url
                          setOpen(true);
                          setEditMode(true);
                        }}
                      >
                        <Edit fontSize={"small"} />
                      </IconButton>
                      </Tooltip>
                    )}
                    {readOnly ? (
                      ""
                    ) : (
                      <Tooltip title="Delete">
                      <IconButton onClick={() => handleDelete(att.id)}>
                        <DeleteIcon fontSize={"small"} sx={{ color: "red" }} />
                      </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </Box>
                <Divider sx={{ my: 1 }} />
              </div>
            ))}
          </Box>
        </Collapse>

        {/* ===== MODAL: Preview Images =================================== */}
        <Modal open={!!previewTarget} onClose={() => setPreviewTarget(null)}>
          <Paper
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              p: 2,
              maxWidth: "90%",
              maxHeight: "90%",
              overflow: "auto",
            }}
          >
            {previewTarget && (
              <img
                src={previewTarget}
                alt="preview"
                style={{
                  maxWidth: "100%",
                  maxHeight: "80vh",
                  display: "block",
                  margin: "0 auto",
                }}
              />
            )}
          </Paper>
        </Modal>

        {/* ===== MODAL: add / edit =================================== */}
        <Modal open={open} onClose={handleClose}>
          <Paper
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              p: 4,
              width: 400,
              maxHeight: "90vh",
              overflow: "auto",
            }}
          >
            <Typography variant="h6" mb={2}>
              <b> {readOnly ? "View" : "Upload"} File</b>
            </Typography>

            {/* ~~~~ file picker row ~~~~ */}
            {selectedAttachment?.url &&
            editMode &&
            disableFileChangeForExisting &&
            selectedAttachment.file === undefined ? (
              <Typography variant="caption" color="text.secondary">
                File change is not allowed for existing attachments.
              </Typography>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  border: "1px solid #ccc",
                  borderRadius: 1,
                  height: 56,
                  px: 2,
                  py: 1,
                  mt: 2,
                  mb: 2,
                  cursor: "pointer",
                  "&:hover": { borderColor: "#999" },
                }}
                onClick={() => {
                  // avoid opening picker when editing remote + disabled
                  if (
                    selectedAttachment?.url &&
                    disableFileChangeForExisting &&
                    selectedAttachment.file === undefined
                  )
                    return;
                  fileInputRef.current?.click();
                }}
              >
                {/* -------- file name & size ---------- */}
                <Box display="flex" alignItems="center" sx={{ width: "75%" }}>
                  <AttachFileIcon fontSize="small" sx={{ mr: 1 }} />

                  {selectedFile ? (
                    <Box>
                      <Typography variant="body2" fontWeight="bold">
                        {selectedFileSize > 0
                          ? getTrimmedString(
                              selectedFile.name ??
                                selectedFile.file?.name ??
                                "",
                              20
                            )
                          : getTrimmedString(
                              selectedAttachment?.title || "",
                              20
                            )}
                      </Typography>
                      {selectedFileSize > 0 && (
                        <Typography variant="caption" color="text.secondary">
                          {(selectedFileSize / 1024).toFixed(2)} KB
                        </Typography>
                      )}
                    </Box>
                  ) : (
                    <Typography variant="caption">Select File…</Typography>
                  )}
                </Box>

                {/* -------- right-side icons ---------- */}
                <Box sx={{ width: "25%", textAlign: "end" }}>
                  {/* Crop icon. Visibility: `.type` matches a raw File,
                      `.mimetype` matches a remote Attachment row.
                      CROP-NOTE: a *local* Attachment row (Edit on a not-yet-
                      uploaded file) has neither, so the icon stays hidden. */}
                  {cropFeature &&
                    selectedFile &&
                    (selectedFile.type?.startsWith("image/") ||
                      selectedFile.mimetype?.startsWith("image/")) && (
                      <IconButton
                        title="Crop Image"
                        onClick={(e) => {
                          e.stopPropagation();
                          setCropTarget({
                            // CROP-NOTE: undefined when selectedFile is a raw
                            // File (fresh pick) — Files have no `.id`, so
                            // updateAttachmentFile later matches no row.
                            id: selectedFile.id,
                            // CROP-NOTE: remote row → signed URL (OK); raw
                            // File → File (OK); local Attachment row → the row
                            // object itself, which isn't a File/URL and breaks
                            // URL.createObjectURL inside ImageCropper.
                            fileOrUrl: selectedFile.url ?? selectedFile,
                          });
                        }}
                      >
                        <Crop fontSize={"small"} color="primary" />
                      </IconButton>
                    )}

                  {selectedFile && (
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedFile(null);
                        setSelectedAttachment((p) =>
                          p ? { ...p, file: undefined } : null
                        );
                      }}
                    >
                      <DeleteIcon fontSize="small" sx={{ color: "red" }} />
                    </IconButton>
                  )}
                </Box>

                {/* hidden input */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={acceptAttr}
                  hidden
                  multiple={multiple && !editMode}
                  disabled={
                    !!(
                      selectedAttachment?.url &&
                      disableFileChangeForExisting &&
                      selectedAttachment.file === undefined
                    )
                  }
                  onChange={(e) => {
                    const files = Array.from(e.target.files ?? []).filter(
                      isExtAllowed
                    );
                    e.target.value = "";

                    if (files.length > 1) {
                      onChange([
                        ...attachments,
                        ...files.map((file) => ({
                          id: uuidv4(),
                          title: file.name,
                          description: "",
                          file,
                        })),
                      ]);
                      handleClose();
                      return;
                    }

                    const file = files[0];
                    if (file) {
                      setSelectedFile(file);
                      setSelectedAttachment((prev) =>
                        prev
                          ? { ...prev, file }
                          : { id: uuidv4(), title: "", description: "", file }
                      );
                    }
                  }}
                />
              </Box>
            )}

            {/* Title & Description fields */}
            <TextField
              fullWidth
              label="Title"
              sx={{ mb: 2 }}
              value={selectedAttachment?.title || ""}
              onChange={(e) =>
                setSelectedAttachment((p) =>
                  p ? { ...p, title: e.target.value } : null
                )
              }
            />
            <TextField
              fullWidth
              label="Description"
              multiline
              rows={3}
              value={selectedAttachment?.description || ""}
              onChange={(e) =>
                setSelectedAttachment((p) =>
                  p ? { ...p, description: e.target.value } : null
                )
              }
            />

            {/* ---- modal buttons ---- */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 1,
                mt: 2,
              }}
            >
              <Button onClick={handleClose}>Cancel</Button>
              <Button
                variant="contained"
                onClick={handleUpdate}
                disabled={!editMode && !selectedFile}
              >
                Save
              </Button>
            </Box>
          </Paper>
        </Modal>
      </Box>

      {/* ===== IMAGE CROPPER modal ================================= */}
      <Modal open={!!cropTarget} onClose={() => setCropTarget(null)}>
        <Paper
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            p: 4,
            width: 700,
            height: 700,
            overflow: "auto",
          }}
        >
          {cropTarget && (
            <ImageCropper
              fileOrUrl={cropTarget.fileOrUrl}
              isOpen
              onCancel={() => setCropTarget(null)}
              onSave={(cropped) => {
                // `cropped` is a new PNG File built by ImageCropper.handleSave.
                // Path A — modal draft: shows the cropped file in the Upload
                // modal; persisted only if the user then clicks its Save
                // (handleUpdate).
                setSelectedFile(cropped);
                setSelectedAttachment((p) =>
                  p
                    ? { ...p, file: cropped }
                    : {
                        id: uuidv4(),
                        title: "",
                        description: "",
                        file: cropped,
                      }
                );
                // Path B — parent list, immediately (no modal Save needed).
                // CROP-NOTE: Paths A and B both write the crop, independently:
                // fresh pick → B is a no-op (id undefined), only A applies;
                // editing an existing row → B commits right away, and A may
                // write it again on modal Save.
                updateAttachmentFile(cropTarget.id, cropped);
                setCropTarget(null);
              }}
            />
          )}
        </Paper>
      </Modal>
    </>
  );
};

export default AttachmentManager;
